import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Form, Card, Button, Breadcrumb, Table, Pagination, Badge, Modal, } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { createHolidayType, updateHolidayType, getHolidayTypes } from "../../../../api/onboarddeboard";

const DESCRIPTION_TRUNCATE_LENGTH = 40;

const HolidayTypes = () => {
    const token = localStorage.getItem("token");
    const [typeName, setTypeName] = useState("");
    const [description, setDescription] = useState("");
    const [colourHex, setColourHex] = useState("#6366f1");
    const [status, setStatus] = useState(true);
    const [isPaid, setIsPaid] = useState(false);
    const [isAutoGrant, setIsAutoGrant] = useState(false);
    const [holidayTypes, setHolidayTypes] = useState([]);
    const [filteredTypes, setFilteredTypes] = useState([]);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editId, setEditId] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [sortField, setSortField] = useState("name");
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;
    const [showEditWarningModal, setShowEditWarningModal] = useState(false);
    const [pendingEditType, setPendingEditType] = useState(null);
    const [showDeactivateModal, setShowDeactivateModal] = useState(false);
    const [pendingDeactivateType, setPendingDeactivateType] = useState(null);
    const [futureDatedEntries, setFutureDatedEntries] = useState([]);
    const [deactivateReason, setDeactivateReason] = useState("");
    const [showDescModal, setShowDescModal] = useState(false);
    const [selectedDescription, setSelectedDescription] = useState({ name: "", text: "" });

    const fetchHolidayTypes = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await getHolidayTypes(token);
            if (response && response.status === 200) {
                const normalized = (response.data || []).map((item) => ({
                    id: item.id,
                    typeName: item.typeName,
                    description: item.description || "",
                    colour: item.colourHex || "#6366f1",
                    active: item.status,
                    isPaid: item.isPaid || false,
                    isAutoGrant: item.isAutoGrant || false,
                    linkedEntriesCount: item.linkedEntriesCount || 0,
                    futureDatedEntries: item.futureDatedEntries || [],
                    createdAt: item.createdDate || new Date().toISOString(),
                    updatedAt: item.modifiedDate || new Date().toISOString(),
                }));
                setHolidayTypes(normalized);
            } else {
                toast.error("Failed to fetch Holiday Types.");
            }
        } catch (error) {
            toast.error("Error loading Holiday Types. Please try again.");
        } finally {
            setIsLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (token) fetchHolidayTypes();
    }, [token, fetchHolidayTypes]);

    useEffect(() => {
        let filtered = [...holidayTypes];
        if (searchQuery.trim()) {
            filtered = filtered.filter((type) =>
                type.typeName.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }
        if (statusFilter !== "all") {
            const isActive = statusFilter === "active";
            filtered = filtered.filter((type) => type.active === isActive);
        }
        if (sortField === "name") {
            filtered.sort((a, b) => a.typeName.localeCompare(b.typeName));
        } else if (sortField === "status") {
            filtered.sort((a, b) => {
                if (a.active === b.active) return a.typeName.localeCompare(b.typeName);
                return a.active ? -1 : 1;
            });
        }
        setFilteredTypes(filtered);
        setCurrentPage(1);
    }, [holidayTypes, searchQuery, statusFilter, sortField]);

    const validateDuplicatesLive = useCallback(() => {
        if (!holidayTypes || holidayTypes.length === 0) return;
        if (typeName.trim()) {
            const duplicate = holidayTypes.some(
                (type) =>
                    type.typeName.toLowerCase() === typeName.trim().toLowerCase() &&
                    (!isEditMode || type.id !== editId)
            );
            if (duplicate) {
                setErrors((prev) => ({ ...prev, typeName: "A Holiday Type with this name already exists." }));
            } else {
                setErrors((prev) => ({
                    ...prev,
                    typeName: prev.typeName === "A Holiday Type with this name already exists." ? "" : prev.typeName,
                }));
            }
        }
    }, [typeName, holidayTypes, isEditMode, editId]);

    useEffect(() => {
        validateDuplicatesLive();
    }, [validateDuplicatesLive]);

    const handleTypeNameChange = (e) => {
    const value = e.target.value;
    setTypeName(value);
    const trimmedValue = value.trim();

    if (/^\s+$/.test(value)) {
        setErrors((prev) => ({ ...prev, typeName: "Type Name cannot be only spaces." }));
        return;
    }
    if (/^\d+$/.test(trimmedValue)) {
        setErrors((prev) => ({ ...prev, typeName: "Type Name cannot be only numeric." }));
        return;
    }
    if (/^[^a-zA-Z0-9]+$/.test(trimmedValue)) {
        setErrors((prev) => ({ ...prev, typeName: "Type Name cannot be only special characters." }));
        return;
    }
    if (
        /^[^a-zA-Z]*$/.test(trimmedValue) &&
        /[0-9]/.test(trimmedValue) &&
        /[^a-zA-Z0-9]/.test(trimmedValue)
    ) {
        setErrors((prev) => ({ ...prev, typeName: "Type Name cannot be only numbers and special characters." }));
        return;
    }
    if (trimmedValue.length > 100) {
        setErrors((prev) => ({ ...prev, typeName: "Type Name cannot exceed 100 characters." }));
        return;
    }
    setErrors((prev) => ({ ...prev, typeName: "" }));
};
    const handleDescriptionChange = (e) => {
        const value = e.target.value;
        if (value.length > 500) {
            setErrors((prev) => ({ ...prev, description: "Description cannot exceed 500 characters." }));
            return;
        }
        setDescription(value);
        if (value.trim() && !/[a-zA-Z]/.test(value)) {
            setErrors((prev) => ({ ...prev, description: "Description must contain at least one letter." }));
            return;
        }
        setErrors((prev) => ({ ...prev, description: "" }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = {};
       if (/^\s+$/.test(typeName)) {
    newErrors.typeName = "Type Name cannot be only spaces.";
} else if (!typeName.trim()) {
    newErrors.typeName = "Type Name is required.";
} else if (/^\d+$/.test(typeName.trim())) {
    newErrors.typeName = "Type Name cannot be only numeric.";
} else if (/^[^a-zA-Z0-9]+$/.test(typeName.trim())) {
    newErrors.typeName = "Type Name cannot be only special characters.";
} else if (
    /^[^a-zA-Z]*$/.test(typeName.trim()) &&
    /[0-9]/.test(typeName.trim()) &&
    /[^a-zA-Z0-9]/.test(typeName.trim())
) {
    newErrors.typeName = "Type Name cannot be only numbers and special characters.";
} else if (typeName.trim().length > 100) {
    newErrors.typeName = "Type Name cannot exceed 100 characters.";
} else {
            const duplicate = holidayTypes.some(
                (type) =>
                    type.typeName.toLowerCase() === typeName.trim().toLowerCase() &&
                    (!isEditMode || type.id !== editId)
            );
            if (duplicate) newErrors.typeName = "A Holiday Type with this name already exists.";
        }
        if (description.length > 500) newErrors.description = "Description cannot exceed 500 characters.";
        if (description.trim() && !/[a-zA-Z]/.test(description.trim())) {
            newErrors.description = "Description must contain at least one letter.";
        }
        if (!colourHex) newErrors.colourHex = "Colour is required.";

        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;

        const requestBody = {
            typeName: typeName.trim(),
            description: description.trim(),
            colourHex: colourHex,
            status: status,
            isPaid: isPaid,
            isAutoGrant: isAutoGrant,
        };

        setIsSubmitting(true);
        try {
            if (isEditMode) {
                const response = await updateHolidayType(editId, requestBody, token);
                if (response && response.status === 200) {
                    toast.success("Holiday Type updated successfully!");
                    await fetchHolidayTypes();
                    handleCancel();
                } else {
                    toast.error("Failed to update Holiday Type.");
                }
            } else {
                const response = await createHolidayType(requestBody, token);
                if (response && response.status === 201) {
                    toast.success("Holiday Type created successfully!");
                    await fetchHolidayTypes();
                    handleCancel();
                } else {
                    toast.error("Failed to create Holiday Type.");
                }
            }
        } catch (error) {
            const errorMsg =
                error?.response?.data?.message ||
                `Failed to ${isEditMode ? "update" : "create"} Holiday Type. Please try again.`;
            toast.error(errorMsg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEdit = (type) => {
        if (type.linkedEntriesCount > 0) {
            setPendingEditType(type);
            setShowEditWarningModal(true);
        } else {
            applyEdit(type);
        }
    };

    const applyEdit = (type) => {
        setIsEditMode(true);
        setEditId(type.id);
        setTypeName(type.typeName);
        setDescription(type.description || "");
        setColourHex(type.colour);
        setStatus(type.active);
        setIsPaid(type.isPaid || false);
        setIsAutoGrant(type.isAutoGrant || false);
        setErrors({});
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const confirmEditWithWarning = () => {
        setShowEditWarningModal(false);
        applyEdit(pendingEditType);
        setPendingEditType(null);
    };

    const handleDeactivate = (type) => {
        setPendingDeactivateType(type);
        setFutureDatedEntries(type.futureDatedEntries || []);
        setDeactivateReason("");
        setShowDeactivateModal(true);
    };

    const confirmDeactivate = async () => {
        try {
            const requestBody = {
                typeName: pendingDeactivateType.typeName,
                description: pendingDeactivateType.description,
                colourHex: pendingDeactivateType.colour,
                status: false,
                isPaid: pendingDeactivateType.isPaid,
                isAutoGrant: pendingDeactivateType.isAutoGrant,
            };
            const response = await updateHolidayType(pendingDeactivateType.id, requestBody, token);
            if (response && response.status === 200) {
                toast.success("Holiday Type deactivated successfully!");
                await fetchHolidayTypes();
            } else {
                toast.error("Failed to deactivate Holiday Type.");
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to deactivate Holiday Type.");
        } finally {
            setShowDeactivateModal(false);
            setPendingDeactivateType(null);
            setFutureDatedEntries([]);
            setDeactivateReason("");
        }
    };

    const handleCancel = () => {
        setTypeName("");
        setDescription("");
        setColourHex("#6366f1");
        setStatus(true);
        setIsPaid(false);
        setIsAutoGrant(false);
        setIsEditMode(false);
        setEditId(null);
        setErrors({});
    };

    const getPaginatedData = () => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredTypes.slice(startIndex, startIndex + itemsPerPage);
    };
    const totalPages = Math.ceil(filteredTypes.length / itemsPerPage);

    const handleExportCSV = () => {
        const headers = ["Type Name", "Description", "Colour", "Paid", "Auto Grant", "Status"];
        const csvData = filteredTypes.map((type) => [
            type.typeName,
            type.description || "",
            type.colour,
            type.isPaid ? "Paid" : "Unpaid",
            type.isAutoGrant ? "Yes" : "No",
            type.active ? "Active" : "Inactive",
        ]);
        let csv = headers.join(",") + "\n";
        csvData.forEach((row) => { csv += row.map((cell) => `"${cell}"`).join(",") + "\n"; });
        const blob = new Blob([csv], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `holiday-types-${new Date().toISOString().split("T")[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const toggleStyle = {
        wrapper: { display: "flex", alignItems: "center", gap: "10px", marginTop: "8px" },
        label: { fontSize: "14px", color: "#555", minWidth: "52px" },
    };

    const DescriptionCell = ({ type }) => {
        const text = type.description;
        if (!text) return <span className="text-muted">-</span>;

        const isTruncated = text.length > DESCRIPTION_TRUNCATE_LENGTH;
        const displayText = isTruncated
            ? text.slice(0, DESCRIPTION_TRUNCATE_LENGTH) + "..."
            : text;

        return (
            <span>
                {displayText}
                {isTruncated && (
                    <>
                        {" "}
                        <span
                            style={{
                                color: "#2E5CB8",
                                fontSize: "12px",
                                cursor: "pointer",
                                fontWeight: 500,
                                textDecoration: "underline",
                                whiteSpace: "nowrap",
                            }}
                            onClick={() => {
                                setSelectedDescription({ name: type.typeName, text });
                                setShowDescModal(true);
                            }}
                        >
                            Read more
                        </span>
                    </>
                )}
            </span>
        );
    };

    return (
    <Container fluid className="p-3 p-md-6">
        <style>{`
            .leave-toggle .form-check-input:checked {
                background-color: #2E5CB8 !important;
                border-color: #2E5CB8 !important;
            }
            .leave-toggle .form-check-input:focus {
                border-color: #2E5CB8 !important;
                box-shadow: 0 0 0 0.1rem rgba(46, 92, 184, 0.35) !important;
            }
            .lc-breadcrumb a {
                text-decoration: none !important;
                color: #2E5CB8 !important;
            }
            .lc-breadcrumb .breadcrumb-item.active {
                color: #6c757d !important;
            }
            .pagination .page-item.active .page-link {
                background-color: #2E5CB8 !important;
                border-color: #2E5CB8 !important;
            }
            @media (max-width: 575.98px) {
                .holidaytype-search-controls .form-control,
                .holidaytype-search-controls .form-select {
                    width: 100% !important;
                }
            }
        `}</style>
<h1 className="page-main-heading" style={{ marginBottom: 12 }}>
            Holiday Types
        </h1>
            <ToastContainer position="top-right" />
            <Breadcrumb className="lc-breadcrumb">
                <Breadcrumb.Item href="#">Leave</Breadcrumb.Item>
                <Breadcrumb.Item href="#">Masters</Breadcrumb.Item>
                <Breadcrumb.Item active>Holiday Types</Breadcrumb.Item>
            </Breadcrumb>

            {/* ── Form Card ── */}
            <Row className="mb-4">
                <Col xl={12}>
                    <Card>
                        <Card.Body>
                            <Form>
                                <Row>
                                    {/* Left Column */}
                                    <Col xs={12} md={6}>
                                        <Row className="mb-3">
                                            <Form.Label className="col-12 col-sm-3 col-form-label form-label">
                                                Type Name<span className="text-danger">*</span>
                                            </Form.Label>
                                            <Col xs={12} sm={9}>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter Type Name"
                                                    value={typeName}
                                                    onChange={handleTypeNameChange}
                                                />
                                                {errors.typeName && <p style={{ color: "red", fontSize: "13px" }}>{errors.typeName}</p>}
                                            </Col>
                                        </Row>

                                        <Row className="mb-3">
                                            <Form.Label className="col-12 col-sm-3 col-form-label form-label">
                                                Description
                                            </Form.Label>
                                            <Col xs={12} sm={9}>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={3}
                                                    placeholder="Enter description"
                                                    value={description}
                                                    onChange={handleDescriptionChange}
                                                />
                                                {errors.description && <p style={{ color: "red", fontSize: "13px" }}>{errors.description}</p>}
                                            </Col>
                                        </Row>

                                        <Row className="mb-3">
                                            <Form.Label className="col-12 col-sm-3 col-form-label form-label">
                                                Active
                                            </Form.Label>
                                            <Col xs={12} sm={9}>
                                                <div style={toggleStyle.wrapper} className="leave-toggle">
                                                    <Form.Check
                                                        type="switch"
                                                        id="status-toggle"
                                                        checked={status}
                                                        onChange={(e) => setStatus(e.target.checked)}
                                                    />
                                                    <span style={toggleStyle.label}>
                                                        {"Check if Active"}
                                                    </span>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Col>

                                    {/* Right Column */}
                                    <Col xs={12} md={6}>
                                        <Row className="mb-3">
                                            <Form.Label className="col-12 col-sm-3 col-form-label form-label">
                                                Colour<span className="text-danger">*</span>
                                            </Form.Label>
                                            <Col xs={12} sm={9}>
                                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                                    <Form.Control
                                                        type="color"
                                                        value={colourHex}
                                                        onChange={(e) => {
                                                            setColourHex(e.target.value);
                                                            setErrors((prev) => ({ ...prev, colourHex: "" }));
                                                        }}
                                                        style={{ width: "60px", height: "40px" }}
                                                    />
                                                    <Form.Control
                                                        type="text"
                                                        value={colourHex}
                                                        onChange={(e) => {
                                                            if (/^#[0-9A-Fa-f]{0,6}$/.test(e.target.value))
                                                                setColourHex(e.target.value);
                                                        }}
                                                        placeholder="#6366f1"
                                                        style={{ flex: "1 1 120px", minWidth: "120px" }}
                                                    />
                                                </div>
                                                {errors.colourHex && <p style={{ color: "red", fontSize: "13px" }}>{errors.colourHex}</p>}
                                            </Col>
                                        </Row>

                                        <Row className="mb-3">
                                            <Form.Label className="col-12 col-sm-3 col-form-label form-label">
                                                Is Paid
                                            </Form.Label>
                                            <Col xs={12} sm={9}>
                                                <div style={toggleStyle.wrapper} className="leave-toggle">
                                                    <Form.Check
                                                        type="switch"
                                                        id="ispaid-toggle"
                                                        checked={isPaid}
                                                        onChange={(e) => setIsPaid(e.target.checked)}
                                                    />
                                                    <span style={toggleStyle.label}>
                                                        {isPaid ? "Paid" : "Unpaid"}
                                                    </span>
                                                </div>
                                            </Col>
                                        </Row>

                                        <Row className="mb-3">
                                            <Form.Label className="col-12 col-sm-3 col-form-label form-label">
                                                Auto Grant
                                            </Form.Label>
                                            <Col xs={12} sm={9}>
                                                <div style={toggleStyle.wrapper} className="leave-toggle">
                                                    <Form.Check
                                                        type="switch"
                                                        id="autogrant-toggle"
                                                        checked={isAutoGrant}
                                                        onChange={(e) => setIsAutoGrant(e.target.checked)}
                                                    />
                                                    <span style={toggleStyle.label}>
                                                        {isAutoGrant ? "Yes" : "No"}
                                                    </span>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Col>

                                    {/* Buttons */}
                                    <Row className="align-items-center">
                                        <Col xs={12} className="mt-4 d-flex align-items-center justify-content-center flex-wrap gap-2">
                                            <Button
                                                variant="primary"
                                                type="button"
                                                onClick={handleSubmit}
                                                disabled={isSubmitting}
                                                style={{ backgroundColor: "#2E5CB8", borderColor: "#2E5CB8" }}
                                            >
                                                {isSubmitting
                                                    ? isEditMode ? "Updating..." : "Submitting..."
                                                    : isEditMode ? "Update" : "Submit"}
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                type="reset"
                                                style={{ marginLeft: "10px" }}
                                                onClick={handleCancel}
                                            >
                                                Cancel
                                            </Button>
                                        </Col>
                                    </Row>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* ── List Card ── */}
            <Row>
                <Col xl={12}>
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <h5 className="mb-0">Holiday Types List</h5>
                            <Button variant="outline-primary" size="sm" onClick={handleExportCSV} style={{ color: "#2E5CB8", borderColor: "#2E5CB8" }}>
                                <i className="bi bi-download me-2"></i>Export CSV
                            </Button>
                        </Card.Header>
                        <Card.Body>
                            <Row className="mb-3 justify-content-end align-items-center holidaytype-search-controls g-2">
                                <Col xs={12} sm="auto">
                                    <Form.Control
                                        type="text"
                                        placeholder="Search by Type Name..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        style={{ width: "180px", maxWidth: "100%", fontSize: "13px", height: "32px", padding: "4px 10px" }}
                                    />
                                </Col>
                                <Col xs={6} sm="auto">
                                    <Form.Select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        style={{ width: "130px", maxWidth: "100%", fontSize: "13px", height: "32px", padding: "4px 10px" }}
                                    >
                                        <option value="all">All Status</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </Form.Select>
                                </Col>
                                <Col xs={6} sm="auto">
                                    <Form.Select
                                        value={sortField}
                                        onChange={(e) => setSortField(e.target.value)}
                                        style={{ width: "150px", maxWidth: "100%", fontSize: "13px", height: "32px", padding: "4px 10px" }}
                                    >
                                        <option value="name">Sort: Name (A–Z)</option>
                                        <option value="status">Sort: Status</option>
                                    </Form.Select>
                                </Col>
                                <Col xs="auto">
                                    <Badge bg="secondary">Total: {filteredTypes.length}</Badge>
                                </Col>
                            </Row>
                            <div className="table-responsive">
                                <Table striped bordered hover responsive>
                                    <thead>
                                        <tr>
                                            <th style={{ backgroundColor: "#2E5CB8", color: "white", width: "55px", textAlign: "center" }}>Sr.No</th>
                                            <th style={{ backgroundColor: "#2E5CB8", color: "white" }}>Type Name</th>
                                            <th style={{ backgroundColor: "#2E5CB8", color: "white" }}>Description</th>
                                            <th style={{ backgroundColor: "#2E5CB8", color: "white", width: "110px" }}>Colour</th>
                                            <th style={{ backgroundColor: "#2E5CB8", color: "white", width: "100px" }}>Paid</th>
                                            <th style={{ backgroundColor: "#2E5CB8", color: "white", width: "110px" }}>Auto Grant</th>
                                            <th style={{ backgroundColor: "#2E5CB8", color: "white", width: "100px" }}>Status</th>
                                            <th style={{ backgroundColor: "#2E5CB8", color: "white", width: "90px" }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isLoading ? (
                                            <tr>
                                                <td colSpan="8" className="text-center py-4">
                                                    <div className="text-muted">
                                                        <i className="bi bi-hourglass-split fs-3 d-block mb-2"></i>
                                                        Loading...
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : getPaginatedData().length > 0 ? (
                                            getPaginatedData().map((type, index) => (
                                                <tr key={type.id}>
                                                    <td style={{ textAlign: "center" }}>
                                                        {(currentPage - 1) * itemsPerPage + index + 1}
                                                    </td>
                                                    <td>{type.typeName || "-"}</td>
                                                    <td>
                                                        <DescriptionCell type={type} />
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div style={{
                                                                width: "24px", height: "24px",
                                                                backgroundColor: type.colour,
                                                                borderRadius: "4px",
                                                                border: "1px solid #ddd",
                                                                flexShrink: 0,
                                                            }} />
                                                            <small className="text-muted">{type.colour}</small>
                                                        </div>
                                                    </td>
                                                    <td>{type.isPaid ? "Paid" : "Unpaid"}</td>
                                                    <td>{type.isAutoGrant ? "Yes" : "No"}</td>
                                                    <td>{type.active ? "Active" : "Inactive"}</td>
                                                    <td>
                                                        <button
                                                            className="btn btn-secondary"
                                                            type="button"
                                                            style={{ padding: "3px 8px", fontSize: "12px" }}
                                                            onClick={() => handleEdit(type)}
                                                        >
                                                            <i className="bi bi-pencil-fill me-1" />Edit
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="8" className="text-center py-4">
                                                    <div className="text-muted">
                                                        <i className="bi bi-inbox fs-3 d-block mb-2"></i>
                                                        No holiday types found
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {totalPages > 1 && (
                                <div className="d-flex justify-content-end">
                                    <Pagination className="flex-wrap">
                                        <Pagination.Prev onClick={() => setCurrentPage((p) => p - 1)} disabled={currentPage === 1} />
                                        {[...Array(totalPages).keys()].map((n) => (
                                            <Pagination.Item key={n + 1} active={n + 1 === currentPage} onClick={() => setCurrentPage(n + 1)}>
                                                {n + 1}
                                            </Pagination.Item>
                                        ))}
                                        <Pagination.Next onClick={() => setCurrentPage((p) => p + 1)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* ── Edit Warning Modal ── */}
            <Modal show={showEditWarningModal} onHide={() => setShowEditWarningModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="bi bi-exclamation-triangle-fill text-warning me-2" />Edit Holiday Type
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        <strong>{pendingEditType?.typeName}</strong>
                        <strong>{pendingEditType?.linkedEntriesCount} holiday {pendingEditType?.linkedEntriesCount === 1 ? "entry" : "entries"}</strong>.
                    </p>
                    <p className="mb-0 text-muted">
                        Any changes (name, colour) will propagate to all associated holiday entries. Do you want to proceed?
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditWarningModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={confirmEditWithWarning}>Yes, Edit</Button>
                </Modal.Footer>
            </Modal>

            {/* ── Deactivate Modal ── */}
            <Modal show={showDeactivateModal} onHide={() => setShowDeactivateModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="bi bi-exclamation-triangle-fill text-danger me-2" />Deactivate Holiday Type
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <strong>{pendingDeactivateType?.typeName}</strong>
                    <p className="text-muted mb-3">
                        Inactive types will no longer appear in dropdowns. Existing records will be preserved.
                    </p>
                    {futureDatedEntries.length > 0 && (
                        <div className="alert alert-warning">
                            <strong><i className="bi bi-calendar-x me-1" />Warning:</strong>{" "}
                            This type has {futureDatedEntries.length} future-dated {futureDatedEntries.length === 1 ? "entry" : "entries"} that will be affected:
                            <ul className="mb-0 mt-2">
                                {futureDatedEntries.map((entry, idx) => (
                                    <li key={idx}><strong>{entry.date}</strong> — {entry.name}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <Form.Group>
                        <Form.Label>Reason for Deactivation <span className="text-muted">(optional)</span></Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="Enter reason"
                            value={deactivateReason}
                            onChange={(e) => setDeactivateReason(e.target.value)}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeactivateModal(false)}>Cancel</Button>
                    <Button variant="danger" onClick={confirmDeactivate}>Deactivate</Button>
                </Modal.Footer>
            </Modal>

            {/* ── Description Read More Modal ── */}
            <Modal show={showDescModal} onHide={() => setShowDescModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: "16px" }}>
                        <i className="bi bi-card-text me-2 text-primary" />
                        {selectedDescription.name}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ marginBottom: 0, lineHeight: "1.7", whiteSpace: "pre-wrap" }}>
                        {selectedDescription.text}
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDescModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default HolidayTypes;
